// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';
import Icon from 'presentations/views/components/atoms/Icon';
import FlowSettingMenu, {
  MenuOptions,
} from 'presentations/views/components/molecules/FloatSettingMenu';
import {
  eventTypePath,
  hostEventTypeEditPath,
  teamPath,
  userPath,
  webPath,
} from 'presentations/routes/path';
import { spacing, fontSize } from 'presentations/views/styles';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import Menu from 'presentations/views/components/atoms/Menu';
import HostEventTypeListItem, {
  LoadingComponent,
} from 'presentations/views/components/molecules/HostEventTypeListItem';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Role } from 'domains/models/event_type_user/role';
import { Role as TeamUserRole } from 'domains/models/team_user/role';
import { viewerOfEventType } from 'presentations/views/helpers/event_type';
import { ViewerItem } from 'presentations/api/schema/user';
import { Status } from 'domains/models/event_type/status';
import {
  isAndroid,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';
import EventTypeShareMenu, {
  EventTypeShareOptionIndex,
} from 'presentations/views/components/molecules/EventTypeShareMenu';
import { DisplayType } from 'domains/models/event_type/displayType';
import { useRouter } from 'next/router';

export interface Props {
  eventTypes: EventTypeListItem[];
  onSelectMenu?: any;
  onSelectShareMenu?: any;
  onClickShowProfile?: (
    eventType: EventTypeListItem,
    isShowProfile: boolean,
  ) => void;
  viewer: ViewerItem;
  canAddEventType: boolean;
  isLoading?: boolean;
}

export enum EventTypeOptionIndex {
  edit = 0,
  copy = 1,
  delete = 2,
  divider = 3,
  activation = 4,
}

export enum OneOffOptionIndex {
  delete = 0,
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isiOSApp = isiOSAppFunc();
  const router = useRouter();

  const [isShowProfile, setIsShowProfile] = useState<null | boolean>(null);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [anchorShare, setAnchorShare] = useState<null | HTMLElement>(null);
  const [
    anchorEventType,
    setAnchorEventType,
  ] = useState<null | EventTypeListItem>(null);
  const [copyAnchor, setCopyAnchor] = useState<null | HTMLElement>(null);
  const [
    copyAnchorEventType,
    setCopyAnchorEventType,
  ] = useState<null | EventTypeListItem>(null);

  const onCloseMenu = () => {
    setAnchor(null);
    setAnchorEventType(null);
  };

  const onCloseCopyMenu = () => {
    setCopyAnchor(null);
    setCopyAnchorEventType(null);
  };

  const onClickMenu = (e: any, eventType: EventTypeListItem) => {
    e.preventDefault();
    setAnchor(e.currentTarget);
    setAnchorEventType(eventType);
  };

  const onClickCopyMenu = (e: any, eventType: EventTypeListItem) => {
    e.preventDefault();
    setCopyAnchor(e.currentTarget);
    setCopyAnchorEventType(eventType);
  };

  const onClickShareMenu = (
    e: SyntheticEvent<HTMLAnchorElement>,
    eventType: EventTypeListItem,
  ) => {
    e.preventDefault();
    setAnchorShare(e.currentTarget);
    setAnchorEventType(eventType);
    setIsShowProfile(eventType.displayType === DisplayType.Public);
  };

  const onCloseShareMenu = () => {
    setAnchorShare(null);
    setAnchorEventType(null);
  };

  const onSelectMenu = async (selectedIndex: number) => {
    if (!anchorEventType) return;
    setAnchor(null);
    props.onSelectMenu(anchorEventType, selectedIndex);
  };

  const onSelectShareMenu = async (selectedIndex: number) => {
    if (!anchorEventType) return;
    if (selectedIndex === EventTypeShareOptionIndex.profile) {
      return;
    }
    setAnchorShare(null);
    props.onSelectShareMenu(anchorEventType, selectedIndex);
  };

  const onSelectCopyMenu = (selectedIndex: number) => {
    setCopyAnchor(null);
  };

  const onClickShowProfile = () => {
    if (!anchorEventType) return;
    if (!props.onClickShowProfile) return;
    if (isShowProfile === null) return;
    setIsShowProfile(!isShowProfile);
    props.onClickShowProfile(anchorEventType, !isShowProfile);
  };

  const onCopyClipboard = () => {
    if (anchorEventType) setAnchorEventType(null);
    if (copyAnchorEventType) setCopyAnchorEventType(null);
  };

  let copyLinkUrl = '';
  if (copyAnchorEventType) {
    copyLinkUrl = webPath(
      eventTypePath({
        teamSlug: props.viewer.team!.slug,
        eventTypeSlug: copyAnchorEventType.slug,
      }),
      router.locale,
    );
  }

  const renderMenu = (eventType: EventTypeListItem, role: Role) => {
    if (!anchorEventType) return null;
    if (anchorEventType.id !== eventType.id) return null;
    if (role > Role.Editor) return null;

    const isEnabled = eventType.status === Status.Enable;

    const options: MenuOptions[] = [
      {
        icon: 'edit',
        label: t('Edit'),
        link: hostEventTypeEditPath(eventType.uid),
      },
      {
        icon: 'file_copy_outlined',
        label: t('Copy'),
      },
      {
        icon: 'delete',
        label: t('Delete'),
      },
      {
        icon: '',
        label: '-',
        divider: true,
      },
      {
        icon: isEnabled ? 'toggle-switch' : 'toggle-switch-off',
        label: t(isEnabled ? 'Deactivation' : 'Activation'),
      },
    ];
    return (
      <FlowSettingMenu
        onCloseMenu={onCloseMenu}
        onSelectMenu={onSelectMenu}
        anchor={anchor}
        options={options}
      />
    );
  };

  return (
    <Container>
      {props.isLoading && props.eventTypes.length === 0 && (
        <>
          <HostEventTypeListItemWrap>
            <LoadingComponent />
          </HostEventTypeListItemWrap>
          <HostEventTypeListItemWrap>
            <LoadingComponent />
          </HostEventTypeListItemWrap>
          <HostEventTypeListItemWrap>
            <LoadingComponent />
          </HostEventTypeListItemWrap>
        </>
      )}
      {props.eventTypes.map((eventType) => {
        let role;
        if (props.viewer.role <= TeamUserRole.Admin) {
          role = Role.Admin;
        } else {
          const viewer = viewerOfEventType(eventType, props.viewer.uid);
          role = viewer ? viewer.role : Role.Viewer;
        }
        return (
          <HostEventTypeListItemWrap key={eventType.uid}>
            <HostEventTypeListItem
              teamSlug={props.viewer.team!.slug}
              eventType={eventType}
              onClickMenu={onClickMenu}
              onClickShareMenu={onClickShareMenu}
              isLoading={props.isLoading}
              role={role}
              type="event-type"
              onClickActivation={() =>
                props.onSelectMenu(eventType, EventTypeOptionIndex.activation)
              }
              onCopy={onCopyClipboard}
              onClickCopyMenu={onClickCopyMenu}
              // onClickOnetimeLink={props.onClickOnetimeLink}
            />
            {renderMenu(eventType, role)}
          </HostEventTypeListItemWrap>
        );
      })}
      {props.eventTypes.length === 0 &&
        !props.isLoading &&
        t(`Empty for event-type`)}
      <Menu
        onClose={onCloseCopyMenu}
        onSelect={onSelectCopyMenu}
        anchor={copyAnchor}
        options={[
          <CopyMenuItem>
            <CopyToClipboard text={copyLinkUrl} onCopy={onCopyClipboard}>
              <MenuItem>
                <MenuIcon name="link" />
                <MenuItemName>{t('Page Url')}</MenuItemName>
              </MenuItem>
            </CopyToClipboard>
          </CopyMenuItem>,
        ]}
      />
      <EventTypeShareMenu
        onCloseMenu={onCloseShareMenu}
        onSelectMenu={onSelectShareMenu}
        onClickShowProfile={onClickShowProfile}
        isShowProfile={isShowProfile}
        anchor={anchorShare}
        viewer={props.viewer}
        isMobileApp={isiOSApp || isAndroid()}
        teamUrl={teamPath(props.viewer.team?.slug!)}
        userUrl={userPath(props.viewer.team?.slug!, props.viewer.slug)}
      />
    </Container>
  );
}

const HostEventTypeListItemWrap = styled.div`
  margin: 0 auto ${spacing(20)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const Container = styled.div`
  width: 100%;
`;
const MenuIcon = styled(Icon)`
  font-size: ${fontSize(20)};
`;
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
`;
const CopyMenuItem = styled.div``;
const MenuItemName = styled.div`
  margin-left: ${spacing(12)};
`;
